import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  ButtonGroup,
  Modal,
  ModalHeader,
  ModalBody,
  Collapse,
} from "reactstrap";
import axios from "axios";
import { API_URL_batch_subproyectos } from "../../constants";
import NewBatchSubproyectoForm from "./NewBacthSubproyectoForm";
import { getAuthHeaders } from "../../utils/auth";

const BatchSubproyectoTable = () => {
  const [batchProyectos, setBatchProyectos] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(null); // For create/edit/clone
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [error, setError] = useState(null);

  // Keep track of which bundle rows are expanded
  // We'll store a list (or set) of batch_codes that are expanded
  const [expandedRows, setExpandedRows] = useState([]);

  useEffect(() => {
    fetchBatchProyectos();
  }, []);

  const fetchBatchProyectos = async () => {
    try {
      const headers = getAuthHeaders();
      const response = await axios.get(API_URL_batch_subproyectos, { headers });
      if (Array.isArray(response.data)) {
        setBatchProyectos(response.data);
      } else {
        setError(`Unexpected data format: ${JSON.stringify(response.data)}`);
      }
    } catch (err) {
      console.error("Error fetching batch proyectos:", err);
      setError("Failed to fetch batch proyectos. Please try again later.");
    }
  };

  // Toggle the modal on/off
  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  // Toggle expand/collapse for a given batchCode
  const toggleExpand = (batchCode) => {
    setExpandedRows((prevExpanded) => {
      // If already expanded, remove it (collapse)
      if (prevExpanded.includes(batchCode)) {
        return prevExpanded.filter((code) => code !== batchCode);
      }
      // Otherwise, add it
      return [...prevExpanded, batchCode];
    });
  };

  // Check if a row is expanded
  const isRowExpanded = (batchCode) => expandedRows.includes(batchCode);

  // Open the modal for editing a specific batch
  const handleEditBatch = (batch) => {
    setSelectedBatch(batch);
    toggleModal();
  };

  // Clone the entire batch (including subproyectos)
  const handleCloneBatch = (batch) => {
    const clonedSubproyectos = (batch.subproyectos || []).map((sub) => ({
      ...sub,
      subproyecto_code: "",
      subproyecto_name: sub.subproyecto_name + " (Cloned)",
    }));

    const clonedBatch = {
      ...batch,
      isClone: true,
      batch_code: "",
      batch_name: batch.batch_name + " (Cloned)",
      subproyectos: clonedSubproyectos,
    };

    setSelectedBatch(clonedBatch);
    toggleModal();
  };

  // Confirm and delete a batch
  const handleDeleteBatch = async (batchCode) => {
    const confirmDelete = window.confirm(
      "Estas seguro que quieres borrar este Bundle?"
    );

    if (confirmDelete) {
      try {
        const headers = getAuthHeaders();
        await axios.delete(`${API_URL_batch_subproyectos}${batchCode}/`, { headers });
        alert("Exito! Bundle borrado exitosamente.");
        fetchBatchProyectos(); // Refresh data after deletion
      } catch (err) {
        console.error("Error deleting batch:", err);
        alert("Hubo un error al borrar el Bundle. Por favor intenta de nuevo.");
      }
    }
  };

  // Reset state after form submission
  const resetState = () => {
    setSelectedBatch(null);
    toggleModal();
    fetchBatchProyectos();
  };

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <div>
      <h2>Bundles</h2>

      <div className="mb-3">
        <Button
          color="success"
          onClick={() => {
            setSelectedBatch(null);
            toggleModal();
          }}
        >
          Create Nuevo Bundle
        </Button>
      </div>

      <Table bordered responsive>
        <thead>
          <tr>
            <th>Bundle Code</th>
            <th>Bundle</th>
            <th>Bundle con Fecha</th>
            <th>Creado el</th>
            <th style={{ width: "220px" }}>Acciones</th>
          </tr>
        </thead>
        <tbody>
          {batchProyectos.length > 0 ? (
            batchProyectos.map((batch) => {
              const expanded = isRowExpanded(batch.batch_code);

              return (
                <React.Fragment key={batch.batch_code}>
                  <tr>
                    <td>{batch.batch_code}</td>
                    <td>{batch.batch_name || "Unnamed Batch"}</td>
                    <td>
                      {batch.batch_second_name +
                        "-" +
                        new Date(batch.batch_second_date).toLocaleDateString(
                          "en-US",
                          { timeZone: "UTC" }
                        ) || "Unnamed Batch"}
                    </td>
                    <td>{new Date(batch.created_at).toLocaleDateString()}</td>
                    <td>
                      {/* ButtonGroup vertical to stack them, or remove vertical for a row layout */}
                      <ButtonGroup vertical style={{ width: "100%" }}>
                        {/* Toggle expand/collapse */}
                        <Button
                          size="sm"
                          color="secondary"
                          onClick={() => toggleExpand(batch.batch_code)}
                        >
                          {expanded ? "Ocultar Proyectos" : "Ver Más"}
                        </Button>
                        <Button
                          color="primary"
                          size="sm"
                          onClick={() => handleEditBatch(batch)}
                        >
                          Editar
                        </Button>
                        <Button
                          color="danger"
                          size="sm"
                          onClick={() => handleDeleteBatch(batch.batch_code)}
                        >
                          Eliminar
                        </Button>
                        <Button
                          color="info"
                          size="sm"
                          onClick={() => handleCloneBatch(batch)}
                        >
                          Clone
                        </Button>
                      </ButtonGroup>
                    </td>
                  </tr>

                  {/* Expanded Row for Proyectos (subproyectos) */}
                  <tr>
                    <td
                      colSpan={5}
                      style={{ padding: 0, borderTop: "none" }}
                    >
                      <Collapse isOpen={expanded}>
                        {Array.isArray(batch.subproyectos) && batch.subproyectos.length > 0 ? (
                          <Table size="sm" bordered className="m-2">
                            <thead>
                              <tr>
                                <th>Subproyecto</th>
                                <th>Codigo Subproyecto</th>
                                <th>Fecha de Inicio</th>
                                <th>Percentaje de Ingreso</th>
                              </tr>
                            </thead>
                            <tbody>
                              {batch.subproyectos.map((subproyecto, i) => (
                                <tr key={i}>
                                  <td>{subproyecto.subproyecto_name}</td>
                                  <td>{subproyecto.subproyecto_code}</td>
                                  <td>
                                    {new Date(
                                      subproyecto.subproyecto_start_date
                                    ).toLocaleDateString("en-US", {
                                      timeZone: "UTC",
                                    })}
                                  </td>
                                  <td>{subproyecto.revenue_percentage}%</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        ) : (
                          <div className="m-3">No hay subproyectos en este bundle.</div>
                        )}
                      </Collapse>
                    </td>
                  </tr>
                </React.Fragment>
              );
            })
          ) : (
            <tr>
              <td colSpan="5">No Bundle Proyectos found.</td>
            </tr>
          )}
        </tbody>
      </Table>

      {/* Modal for creating/editing/cloning */}
      <Modal isOpen={isModalOpen} toggle={toggleModal}>
        <ModalHeader toggle={toggleModal}>
          {selectedBatch
            ? selectedBatch.isClone
              ? "Clone Bundle"
              : "Edit Bundle"
            : "New Bundle"}
        </ModalHeader>
        <ModalBody>
          <NewBatchSubproyectoForm
            toggle={toggleModal}
            resetState={resetState}
            batch={selectedBatch}
          />
        </ModalBody>
      </Modal>
    </div>
  );
};

export default BatchSubproyectoTable;